import AiChanges from "@tiptap-pro/extension-ai-changes"
import { getSchema, type Extensions } from "@tiptap/core"
import { Document } from "@tiptap/extension-document"
import HardBreak from "@tiptap/extension-hard-break"
import { Text } from "@tiptap/extension-text"
import { TextStyle } from "@tiptap/extension-text-style"
import Typography from "@tiptap/extension-typography"
import { Gapcursor } from "@tiptap/extensions"
import type { Schema } from "@tiptap/pm/model"
import type * as filestack from "filestack-js"
import { FlatListCore } from "tiptap-extension-flat-list"
import TrailingBreak from "tiptap-extension-trailing-break"
import { ExtensionCardGoogleClassroom } from "./extensions/card-google-classroom/card-google-classroom-extension"
import { ExtensionCardTitle } from "./extensions/card-title/card-title-extension"
import {
  CARD_VALUE_SCHEMA,
  ExtensionTitledCard,
  ExtensionUntitledCard,
} from "./extensions/cards/card-extensions"
import { CpColor } from "./extensions/color/color-extension"
import { ExtensionCommentContent } from "./extensions/comment-content/comment-content-extension"
import {
  ExtensionDateAnnotationNote,
  ExtensionDateAnnotationNoteTitle,
} from "./extensions/date-annotation-note/date-annotation-note-extension"
import { DragMechanicsSelection } from "./extensions/drag-mechanics-selection/drag-mechanics-selection-extension"
import { CpDropcursor } from "./extensions/drop-cursor/drop-cursor-extension"
import { ExtensionFileUpload } from "./extensions/file-upload/file-upload-extension"
import Image from "./extensions/image-node/image-node-extension"
import { ExtensionLessonTitle } from "./extensions/lesson-title/lesson-title-extension"
import type { LinkDialogOrigin } from "./extensions/link/link-attrs"
import { ExtensionLinkBlock } from "./extensions/link/link-block-extension"
import { ExtensionLinkInline } from "./extensions/link/link-inline-extension"
import { ExtensionOperationLabel } from "./extensions/operation-label/operation-label-extension"
import { ExtensionStandard } from "./extensions/standard/standard-extension"
import {
  ObservedBold,
  ObservedFlatListOrdered,
  ObservedFlatListTask,
  ObservedFlatListUnordered,
  ObservedHeading,
  ObservedHighlight,
  ObservedItalic,
  ObservedParagraph,
  ObservedPaste,
  ObservedStrike,
  ObservedSubscript,
  ObservedSuperscript,
  ObservedUnderline,
  ObservedUndoRedo,
} from "./extensions/stock-bindings/stock-bindings-extension"
import { VersionBoundary } from "./extensions/version-boundary/version-boundary-extension"
import { ExtensionWidgetClickSuppression } from "./extensions/widget-click-suppression/widget-click-suppression-extension"

/**
 * Extensions common to every editor (lesson, note, comment).
 *
 * To avoid separate arrays for editors that do or don't track analytics,
 * we always use the Observed extensions and ExtensionOperationLabel.
 * Label commands become no-ops in an editor that doesn't track analytics
 * (= did not call setAnalyticsRuntime).
 *
 * Structure nodes (card, lessonTitle, etc.) stay per-editor so that the schema itself rejects content that doesn't
 * belong there: a card pasted into a note, a note dropped into a lesson, etc.
 */
function commonExtensions(options: {
  /** The document content specifier. */
  content: string
  /** Callback to open the edit-link dialog, naming the opener the gesture came from. */
  openLinkDialog: ((origin: LinkDialogOrigin) => void) | null
  /** Filestack client, required for attaching files and images. */
  filestackClient: filestack.Client | null
}): Extensions {
  const { content, openLinkDialog, filestackClient } = options

  return [
    // Basics
    Document.extend({ content }),
    ObservedParagraph,
    Text,

    // Normal block nodes
    ObservedHeading.configure({ levels: [1, 2] }),
    FlatListCore,
    ObservedFlatListOrdered.configure({
      getListStyleType: (indent) => {
        // GDocs convention.
        return ["decimal", "lower-alpha", "lower-roman"][indent % 3]
      },
    }),
    ObservedFlatListUnordered.configure({
      getListStyleType: (indent) => {
        // GDocs convention.
        return ["disc", "circle", "square"][indent % 3]
      },
    }),
    ObservedFlatListTask,

    // Common widget blocks
    ExtensionLinkBlock,
    Image.configure({ filestackClient }),
    ExtensionFileUpload.configure({ filestackClient }),

    // Common inline nodes
    HardBreak,
    ExtensionLinkInline.configure({ openLinkDialog }),

    // Marks
    ObservedBold,
    ObservedItalic,
    ObservedUnderline,
    ObservedStrike,
    ObservedSubscript,
    ObservedSuperscript,
    // Before TextStyle due to https://github.com/ueberdosis/tiptap/issues/3427
    ObservedHighlight.configure({ multicolor: true }),
    TextStyle,
    CpColor,

    // Common functionality
    Typography,
    DragMechanicsSelection,
    ExtensionWidgetClickSuppression,
    Gapcursor,
    ObservedPaste,
    ObservedUndoRedo,
    TrailingBreak,
    // Include this so that the `label` command exists even if we aren't recording analytics -
    // see the function typedoc.
    ExtensionOperationLabel,
  ]
}

/** Extensions for the CardStack editors and the headless card helpers. */
export function cardStackExtensions({
  openLinkDialog,
  filestackClient,
  isNonInstructional,
  customDocumentContent,
}: {
  /** Callback to open the edit-link dialog, naming the opener the gesture came from. */
  openLinkDialog: ((origin: LinkDialogOrigin) => void) | null
  /** Filestack client, required for attaching files and images. */
  filestackClient: filestack.Client | null
  isNonInstructional?: boolean
  /** Overrides the content of the doc node. */
  customDocumentContent?: string
}): Extensions {
  return [
    ...commonExtensions({
      content: customDocumentContent ?? (isNonInstructional ? "card*" : `lessonTitle card*`),
      openLinkDialog,
      filestackClient,
    }),

    // CardStack structure
    ...(isNonInstructional ? [] : [ExtensionLessonTitle]),
    ExtensionTitledCard,
    ExtensionCardTitle,
    ExtensionUntitledCard,
    ExtensionCardGoogleClassroom,

    // CardStack-specific extensions
    ExtensionStandard,
    AiChanges,
    VersionBoundary,
    // This is CardStack-specific until we update findNearestDropPos to handle all editor types.
    CpDropcursor.configure({
      // A chunky colored line, so it reads as a drop indicator and not as another
      // card-separator border (styled in unified-tiptap.css).
      width: 3,
      class: "unified-tiptap-drop-cursor",
    }),
  ]
}

/**
 * Extensions for NoteTiptap, the day-note editor.
 * Its structure is a single dateAnnotationNote made of a title and content blocks.
 */
export function noteTiptapExtensions({
  openLinkDialog,
  filestackClient,
}: {
  /** Callback to open the edit-link dialog, naming the opener the gesture came from. */
  openLinkDialog: ((origin: LinkDialogOrigin) => void) | null
  /** Filestack client, required for attaching files and images. */
  filestackClient: filestack.Client | null
}) {
  return [
    ...commonExtensions({ content: "dateAnnotationNote", openLinkDialog, filestackClient }),

    // Note structure
    ExtensionDateAnnotationNote,
    ExtensionDateAnnotationNoteTitle,
  ]
}

/**
 * Extensions for CommentTiptap, the card-comment editor.
 * Its structure is a single commentContent made of content blocks.
 */
export function commentTiptapExtensions({
  openLinkDialog,
  filestackClient,
}: {
  /** Callback to open the edit-link dialog, naming the opener the gesture came from. */
  openLinkDialog: ((origin: LinkDialogOrigin) => void) | null
  /** Filestack client, required for attaching files and images. */
  filestackClient: filestack.Client | null
}) {
  return [
    ...commonExtensions({ content: "commentContent", openLinkDialog, filestackClient }),

    // Comment structure
    ExtensionCommentContent,
  ]
}

let _headlessUnifiedTiptapSchema: Schema | null = null

/**
 * Returns a schema for working with CardStack.proseMirror outside of an editor,
 * e.g., to calculate content length.
 */
export function headlessCardStackSchema(): Schema {
  if (!_headlessUnifiedTiptapSchema) {
    const headlessCardStackExtensions = cardStackExtensions({
      openLinkDialog: null,
      filestackClient: null,
      customDocumentContent: "card*",
    })
    _headlessUnifiedTiptapSchema = getSchema(headlessCardStackExtensions)
  }
  return _headlessUnifiedTiptapSchema
}

let _headlessUnifiedTiptapCardValueExtensions: Extensions | null = null

/**
 * Returns Tiptap extensions for working with a single card's value (the part after the card title)
 * outside of an editor, e.g., to convert it to HTML.
 */
export function headlessCardValueExtensions(): Extensions {
  if (!_headlessUnifiedTiptapCardValueExtensions) {
    _headlessUnifiedTiptapCardValueExtensions = cardStackExtensions({
      openLinkDialog: null,
      filestackClient: null,
      customDocumentContent: CARD_VALUE_SCHEMA,
    })
  }
  return _headlessUnifiedTiptapCardValueExtensions
}
